import { HelmetDexteel } from "@dexteel/mesf-core";
import CheckIcon from "@mui/icons-material/Check";
import { Alert, Snackbar } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	ColDef,
	GetContextMenuItemsParams,
	ICellRendererParams,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { AddButton } from "@/components/buttons/add-button";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { ConfigurationPage } from "../components/ConfigurationPage";
import {
	ConfigurationPanel,
	panelColumn,
	rowMenuColumn,
} from "../components/ConfigurationPanel";
import { ConfigurationSearch } from "../components/ConfigurationSearch";
import { UpsertDeleteRecipient } from "./components/UpsertDeleteRecipient";
import type { EmailRecipient, ModalMode } from "./models/EmailRecipient";
import { getEmailRecipients } from "./repositories/EmailRecipientsRepository";

const gridStyle = {
	height: "calc(100vh - 360px)",
	minHeight: 320,
	width: "100%",
};

export const EmailRecipientsPage = () => {
	const queryClient = useQueryClient();

	const [selected, setSelected] = useState<EmailRecipient | null>(null);
	const [modal, setModal] = useState<ModalMode>("");
	const [notification, setNotification] = useState<string>("");
	const [search, setSearch] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["emailRecipients"],
		queryFn: getEmailRecipients,
	});

	const rows = useMemo(
		() => get(data, "data.tables[0].rows", []) as EmailRecipient[],
		[data],
	);

	const columns = useMemo<ColDef[]>(
		() => [
			{
				field: "DisplayName",
				headerName: "Name",
				flex: 2,
				...panelColumn.text,
			},
			{
				field: "EmailAddress",
				headerName: "Email Address",
				flex: 3,
				...panelColumn.text,
			},
			{
				field: "TemplateCount",
				headerName: "Templates",
				width: 120,
				...panelColumn.number,
			},
			{
				field: "IsActive",
				headerName: "Active",
				width: 100,
				cellRenderer: (p: ICellRendererParams<EmailRecipient>) =>
					p.value ? (
						<CheckIcon color="success" sx={{ fontSize: 18 }} />
					) : (
						<span style={{ color: "rgba(0, 0, 0, 0.26)" }}>{"—"}</span>
					),
			},
			rowMenuColumn,
		],
		[],
	);

	const openNew = useCallback(() => {
		setSelected(null);
		setModal("new");
	}, []);

	const getContextMenuItems = useCallback(
		(e: GetContextMenuItemsParams) => {
			const row = e?.node?.data as EmailRecipient | undefined;
			const result: MenuItemDef[] = [
				{
					name: "New Recipient",
					action: openNew,
					icon: '<i class="fas fa-plus"></i>',
				},
			];
			if (row) {
				result.push(
					{
						name: "Edit Recipient",
						action: () => {
							setSelected(row);
							setModal("edit");
						},
						icon: '<i class="fas fa-edit"></i>',
					},
					{
						name: "Delete Recipient",
						action: () => {
							setSelected(row);
							setModal("delete");
						},
						icon: '<i class="fas fa-trash"></i>',
					},
				);
			}
			return result;
		},
		[openNew],
	);

	const closeModal = (savedMessage?: string) => {
		setModal("");
		if (savedMessage) {
			setNotification(savedMessage);
			queryClient.invalidateQueries({ queryKey: ["emailRecipients"] });
			queryClient.invalidateQueries({ queryKey: ["templateRecipients"] });
		}
	};

	return (
		<>
			<HelmetDexteel title="Email Recipients - Configuration" />
			<ConfigurationPage
				title="Email Recipients"
				description="People a template can notify when it generates work orders. An address is typed here once and picked from the template."
				filters={
					<ConfigurationSearch
						value={search}
						onChange={setSearch}
						placeholder="Name or email"
					/>
				}
			>
				<ConfigurationPanel actions={<AddButton onClick={openNew} />}>
					<div style={gridStyle}>
						<AgGridReact
							rowData={rows}
							columnDefs={columns}
							defaultColDef={globalDefaultColDef}
							rowSelection="single"
							suppressCellFocus={true}
							quickFilterText={search}
							loading={isLoading}
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={(e) => {
								setSelected(e.data ?? null);
								setModal("edit");
							}}
							pagination={false}
							overlayNoRowsTemplate="No email recipients yet"
						/>
					</div>
				</ConfigurationPanel>
			</ConfigurationPage>

			<UpsertDeleteRecipient
				modal={modal}
				recipient={modal === "new" ? null : selected}
				onHide={closeModal}
			/>

			<Snackbar
				open={!!notification}
				autoHideDuration={2500}
				onClose={() => setNotification("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={() => setNotification("")}>
					{notification}
				</Alert>
			</Snackbar>
		</>
	);
};
